import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2, Phone, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COMPANY, SERVICE_OPTIONS } from "./data";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", phone: "", service: "", message: "" };
const field = "h-14 rounded-none border-0 border-b border-white/25 bg-transparent px-0 text-base text-white shadow-none placeholder:text-slate-400 focus-visible:border-terra focus-visible:ring-0 focus-visible:ring-offset-0";

const Field = ({ id, text, children }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">{text}</label>
    {children}
  </div>
);

const Success = ({ reset }) => (
  <div className="flex flex-col items-start py-6" data-testid="contact-success">
    <CheckCircle2 size={52} className="text-emerald-400" />
    <p className="mt-6 font-display text-4xl font-bold uppercase text-white sm:text-5xl">Anfrage erhalten!</p>
    <p className="mt-3 max-w-md text-slate-200">Vielen Dank. Wir melden uns garantiert innerhalb von 24 Stunden bei Ihnen – kostenlos und unverbindlich.</p>
    <div className="mt-8 w-full border-t border-white/10 pt-8">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terra">Dringend?</p>
      <a href={COMPANY.phoneHref} className="btn-primary btn-mega mt-3 w-full justify-center sm:w-auto" data-testid="contact-success-phone"><Phone size={20} /> Direkt anrufen: {COMPANY.phoneDisplay}</a>
    </div>
    <button type="button" onClick={reset} className="btn-ghost mt-4" data-testid="contact-new-request">Weitere Anfrage senden</button>
  </div>
);

export const LeadForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const onService = (e) => {
      if (SERVICE_OPTIONS.includes(e.detail)) setForm((f) => ({ ...f, service: e.detail }));
      setSent(false);
    };
    window.addEventListener("cb:service", onService);
    return () => window.removeEventListener("cb:service", onService);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone.replace(/\D/g, "").length < 6) return toast.error("Bitte geben Sie eine gültige Telefonnummer ein.");
    if (!form.service) return toast.error("Bitte wählen Sie Ihre Wunschleistung aus.");
    if (!BACKEND) return toast.error("Formular derzeit nicht verfügbar – bitte rufen Sie uns an.");
    setSending(true);
    try {
      await axios.post(`${BACKEND}/api/inquiries`, form);
      sessionStorage.setItem("cb-sent", "1");
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(status === 429 ? "Zu viele Anfragen – bitte rufen Sie uns direkt an." : status === 422 ? "Bitte prüfen Sie Ihre Angaben." : "Senden fehlgeschlagen. Bitte rufen Sie uns direkt an.");
    } finally {
      setSending(false);
    }
  };

  if (sent) return <Success reset={() => setSent(false)} />;

  return (
    <form onSubmit={submit} className="grid gap-7 sm:grid-cols-2" data-testid="contact-form">
      <Field id="cf-name" text="Ihr Name *">
        <Input id="cf-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Vor- und Nachname" className={field} autoComplete="name" data-testid="contact-name-input" />
      </Field>
      <Field id="cf-phone" text="Telefon für den Rückruf *">
        <Input id="cf-phone" type="tel" inputMode="tel" required value={form.phone} onChange={set("phone")} placeholder="z. B. 0163 1234567" className={field} autoComplete="tel" data-testid="contact-phone-input" />
      </Field>
      <div className="sm:col-span-2">
        <Field id="cf-service" text="Wunschleistung *">
          <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
            <SelectTrigger id="cf-service" className={`${field} [&>span]:text-left`} data-testid="contact-service-select"><SelectValue placeholder="Bitte wählen" /></SelectTrigger>
            <SelectContent data-lenis-prevent className="rounded-none border-white/10 bg-ink2 text-white">
              {SERVICE_OPTIONS.map((o, i) => <SelectItem key={o} value={o} className="py-3 focus:bg-terra-deep focus:text-white" data-testid={`contact-service-option-${i}`}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field id="cf-message" text="Ihre Nachricht *">
          <Textarea id="cf-message" required minLength={3} rows={3} value={form.message} onChange={set("message")} placeholder="Kurz beschreiben: z. B. Ziegel verrutscht, Dach undicht, Dämmung gewünscht …" className={`${field} min-h-[110px] resize-none py-3`} data-testid="contact-message-input" />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <p className="flex items-start gap-3 border border-emerald-400/30 bg-emerald-400/10 p-4 text-sm font-semibold leading-relaxed text-emerald-50" data-testid="contact-guarantee">
          <ShieldCheck size={20} className="mt-0.5 shrink-0 text-emerald-400" />
          Wir melden uns garantiert innerhalb von 24 Stunden – 100 % kostenlos & unverbindlich.
        </p>
        <button type="submit" disabled={sending} className="btn-primary btn-mega mt-4 w-full justify-center disabled:opacity-70" data-testid="contact-submit-btn">
          {sending ? <><Loader2 size={20} className="animate-spin" /> Wird gesendet …</> : <>Kostenlosen Dachcheck anfordern <ArrowRight size={20} className="btn-arrow hidden sm:block" /></>}
        </button>
        <p className="mt-4 text-xs leading-relaxed text-slate-400">
          Ihre Angaben verwenden wir ausschließlich zur Bearbeitung Ihrer Anfrage. Mehr in unserer{" "}
          <Link to="/datenschutz" target="_blank" className="text-slate-200 underline underline-offset-4 hover:text-white" data-testid="contact-privacy-link">Datenschutzerklärung</Link>.
        </p>
      </div>
    </form>
  );
};
